import React from 'react';
import {
    CardContent,
    Typography,
    TextField,
    Box,
    styled,
    Divider,
    Grid,
    Button,
} from '@mui/material';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { MainCard } from '../components/MainCard';
import { MEXICAN_STATES } from '../constants/mexicanStates';
import RfcFacil from 'rfc-facil';
import * as yup from 'yup';

const CssTextField = styled(TextField)(({ theme }) => ({
    '& .MuiSelect-select': {
        backgroundColor: 'transparent',
    },
    '& label.Mui-focused': {
        color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'transparent',
        '& fieldset': {},
        '&:hover fieldset': {
            borderColor: '#B2BAC2',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#6F7E8C',
        },
        '& input::placeholder': {
            fontSize: 14,
        },
    },
}));

export const ScoreForm = ({ initialValues, setFormState }) => {
    const getRFC = (values) => {
        const birthday = moment(values.fechaNacimiento).format('l');
        const desc = birthday.split('/');

        const RFC = RfcFacil.forNaturalPerson({
            name: `${values.primerNombre} ${values.segundoNombre}`,
            firstLastName: values.apellidoPaterno,
            secondLastName: values.apellidoMaterno,
            day: parseInt(desc[0]),
            month: parseInt(desc[1]),
            year: parseInt(desc[2]),
        });

        return RFC;
    };

    const handleSendScore = async (values) => {
        const RFC = getRFC(values);

        setFormState({ view: 'review', initialValues: { ...values, RFC } });
    };

    const validationSchema = yup.object().shape({
        primerNombre: yup
            .string()
            .matches(
                /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s.]+$/,
                'El nombre solo puede contener letras y signos de acentuación'
            )
            .required('El nombre es requerido'),
        segundoNombre: yup
            .string()
            .matches(
                /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s.]+$/,
                'El nombre solo puede contener letras y signos de acentuación'
            ),
        apellidoPaterno: yup
            .string()
            .matches(
                /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s.]+$/,
                'El apellido solo puede contener letras y signos de acentuación'
            )
            .min(3, 'El apellido paterno debe tener al menos 3 caracteres')
            .required('El primer apellido es requerido'),
        apellidoMaterno: yup
            .string()
            .matches(
                /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s.]+$/,
                'El apellido solo puede contener letras y signos de acentuación'
            )
            .min(3, 'El apellido materno debe tener al menos 3 caracteres'),
        email: yup
            .string()
            .email('El email es inválido')
            .required('El email es requerido'),
        fechaNacimiento: yup
            .date()
            .required('La fecha de nacimiento es requerida'),
        direccion1: yup.string().required('La dirección es requerida'),
        coloniaPoblacion: yup.string().required('La colonia es requerida'),
        ciudad: yup.string().required('La ciudad es requerida'),
        cp: yup.string().required('El CP es requerido'),
        delegacionMunicipio: yup
            .string()
            .required('La delegación es requerida'),
        estado: yup.string().required('El estado es requerido'),
    });

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSendScore}
        >
            {({ setFieldValue, handleChange, touched, errors, values }) => {
                const INPUT_PROPS = {
                    size: 'small',
                    fullWidth: true,
                    required: true,
                    InputLabelProps: { shrink: true },
                    onChange: handleChange,
                    as: CssTextField,
                };

                return (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography textAlign="center" variant="h6">
                                Completa los siguientes datos
                            </Typography>
                            <MainCard title="Formulario de Score">
                                <Typography variant="body1" fontWeight={700}>
                                    Datos Personales
                                </Typography>
                                <Divider sx={{ my: 1 }} />

                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'first-name-field',
                                                }}
                                                label="Primer Nombre"
                                                name="primerNombre"
                                                placeholder="Primer Nombre"
                                                helperText={
                                                    <ErrorMessage name="primerNombre" />
                                                }
                                                error={Boolean(
                                                    touched.primerNombre &&
                                                        errors.primerNombre
                                                )}
                                                value={values.primerNombre}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'second-name-field',
                                                }}
                                                label="Segundo Nombre"
                                                name="segundoNombre"
                                                placeholder="Segundo Nombre"
                                                helperText={
                                                    <ErrorMessage name="segundoNombre" />
                                                }
                                                error={Boolean(
                                                    touched.segundoNombre &&
                                                        errors.segundoNombre
                                                )}
                                                required={false}
                                                value={values.segundoNombre}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'last-name-field',
                                                }}
                                                label="Apellido Paterno"
                                                name="apellidoPaterno"
                                                placeholder="Apellido Paterno"
                                                helperText={
                                                    <ErrorMessage name="apellidoPaterno" />
                                                }
                                                error={Boolean(
                                                    touched.apellidoPaterno &&
                                                        errors.apellidoPaterno
                                                )}
                                                value={values.apellidoPaterno}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'second-last-name-field',
                                                }}
                                                label="Apellido Materno"
                                                name="apellidoMaterno"
                                                placeholder="Apellido Materno"
                                                helperText={
                                                    <ErrorMessage name="apellidoMaterno" />
                                                }
                                                error={Boolean(
                                                    touched.apellidoMaterno &&
                                                        errors.apellidoMaterno
                                                )}
                                                required={false}
                                                value={values.apellidoMaterno}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'email-field',
                                                }}
                                                label="Correo Electrónico"
                                                name="email"
                                                placeholder="Correo Electrónico"
                                                helperText={
                                                    <ErrorMessage name="email" />
                                                }
                                                error={Boolean(
                                                    touched.email &&
                                                        errors.email
                                                )}
                                                required={false}
                                                value={values.email}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DatePicker
                                                disableFuture
                                                value={values.fechaNacimiento}
                                                onChange={(val) =>
                                                    setFieldValue(
                                                        'fechaNacimiento',
                                                        moment(val).format()
                                                    )
                                                }
                                                label="Fecha de Nacimiento"
                                                renderInput={(params) => (
                                                    <Field
                                                        {...INPUT_PROPS}
                                                        size="small"
                                                        {...params}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            'data-testid':
                                                                'birthdate-field',
                                                        }}
                                                        value={
                                                            values.fechaNacimiento
                                                        }
                                                        fullWidth
                                                        error={
                                                            errors.fechaNacimiento
                                                        }
                                                        helperText={
                                                            errors.fechaNacimiento ||
                                                            null
                                                        }
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </MainCard>

                            <MainCard title="Formulario de Score">
                                <Typography variant="body1" fontWeight={700}>
                                    Domicilio
                                </Typography>
                                <Divider sx={{ my: 1 }} />

                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'address-field',
                                                }}
                                                label="Dirección"
                                                name="direccion1"
                                                placeholder="Dirección"
                                                helperText={
                                                    <ErrorMessage name="direccion1" />
                                                }
                                                error={Boolean(
                                                    touched.direccion1 &&
                                                        errors.direccion1
                                                )}
                                                value={values.direccion1}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'neighborhood-field',
                                                }}
                                                label="Colonia"
                                                name="coloniaPoblacion"
                                                placeholder="Colonia"
                                                helperText={
                                                    <ErrorMessage name="coloniaPoblacion" />
                                                }
                                                error={Boolean(
                                                    touched.coloniaPoblacion &&
                                                        errors.coloniaPoblacion
                                                )}
                                                value={values.coloniaPoblacion}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid': 'city-field',
                                                }}
                                                label="Ciudad"
                                                name="ciudad"
                                                placeholder="Ciudad"
                                                helperText={
                                                    <ErrorMessage name="ciudad" />
                                                }
                                                error={Boolean(
                                                    touched.ciudad &&
                                                        errors.ciudad
                                                )}
                                                value={values.ciudad}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid': 'zip-field',
                                                }}
                                                label="Código Postal"
                                                name="cp"
                                                placeholder="Código Postal"
                                                helperText={
                                                    <ErrorMessage name="cp" />
                                                }
                                                error={Boolean(
                                                    touched.cp && errors.cp
                                                )}
                                                value={values.cp}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'delegation-field',
                                                }}
                                                label="Delegación/Municipio"
                                                name="delegacionMunicipio"
                                                placeholder="Delegación/Municipio"
                                                helperText={
                                                    <ErrorMessage name="delegacionMunicipio" />
                                                }
                                                error={Boolean(
                                                    touched.delegacionMunicipio &&
                                                        errors.delegacionMunicipio
                                                )}
                                                value={
                                                    values.delegacionMunicipio
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Field
                                                {...INPUT_PROPS}
                                                inputProps={{
                                                    'data-testid':
                                                        'state-field',
                                                }}
                                                label="Estado"
                                                name="estado"
                                                select
                                                SelectProps={{
                                                    native: true,
                                                }}
                                                helperText={
                                                    <ErrorMessage name="estado" />
                                                }
                                                error={Boolean(
                                                    touched.estado &&
                                                        errors.estado
                                                )}
                                                value={values.estado}
                                            >
                                                <option value="">
                                                    Selecciona tu Estado
                                                </option>
                                                {MEXICAN_STATES.map(
                                                    ({ value, name }) => (
                                                        <option
                                                            data-testid={`state-option-${value}`}
                                                            key={value}
                                                            value={value}
                                                        >
                                                            {name}
                                                        </option>
                                                    )
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </MainCard>

                            <Box display="flex" alignSelf="flex-end">
                                <Button
                                    data-testid="submit-button"
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{
                                        color: '#FFF',
                                        height: 50,
                                        minWidth: 150,
                                    }}
                                >
                                    Siguiente
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};
